import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAppContext } from '../../context/AppContext';
import type { SavedWord } from '../../types';

type Rating = 'again' | 'hard' | 'good' | 'easy';

const DUE_THRESHOLD_MS = 1 * 60 * 60 * 1000;

function computeSRS(word: SavedWord, rating: Rating): Pick<SavedWord, 'interval' | 'ease' | 'dueAt' | 'reviewCount'> {
  const MIN_EASE = 1.3;
  let interval = word.interval || 0;
  let ease = word.ease || 2.5;

  switch (rating) {
    case 'again':
      interval = 1;
      ease = Math.max(MIN_EASE, ease - 0.2);
      break;
    case 'hard':
      interval = Math.max(1, Math.round(interval * 1.2));
      ease = Math.max(MIN_EASE, ease - 0.15);
      break;
    case 'good':
      interval = interval === 0 ? 10 : Math.round(interval * ease);
      break;
    case 'easy':
      interval = interval === 0 ? 60 * 24 : Math.round(interval * ease * 1.3);
      ease = ease + 0.15;
      break;
  }

  return {
    interval,
    ease,
    dueAt: Date.now() + interval * 60 * 1000,
    reviewCount: (word.reviewCount || 0) + 1,
  };
}

function formatInterval(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h`;
  return `${Math.round(minutes / (60 * 24))}d`;
}

const RATING_CONFIG: { key: Rating; label: string; borderColor: string; textColor: string; hoverBg: string }[] = [
  { key: 'again', label: 'Again', borderColor: 'border-red-300 dark:border-red-700', textColor: 'text-red-600 dark:text-red-400', hoverBg: 'hover:bg-red-50 dark:hover:bg-red-900/20' },
  { key: 'hard', label: 'Hard', borderColor: 'border-amber-300 dark:border-amber-700', textColor: 'text-amber-600 dark:text-amber-400', hoverBg: 'hover:bg-amber-50 dark:hover:bg-amber-900/20' },
  { key: 'good', label: 'Good', borderColor: 'border-emerald-300 dark:border-emerald-700', textColor: 'text-emerald-600 dark:text-emerald-400', hoverBg: 'hover:bg-emerald-50 dark:hover:bg-emerald-900/20' },
  { key: 'easy', label: 'Easy', borderColor: 'border-blue-300 dark:border-blue-700', textColor: 'text-blue-600 dark:text-blue-400', hoverBg: 'hover:bg-blue-50 dark:hover:bg-blue-900/20' },
];

export default function FlashcardReview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { decks, savedWords, updateWordSRS, updateDeckLastReviewed } = useAppContext();
  const deck = decks.find(d => d.id === id);
  const allWords = savedWords.filter(w => w.deckId === id);

  // Snapshot the initial review queue once on mount — stable card data
  // that won't reshuffle when Firestore updates come in.
  const initialQueue = useMemo(() => {
    const dueThreshold = Date.now() + DUE_THRESHOLD_MS;
    return allWords
      .filter(w => (w.dueAt || 0) <= dueThreshold)
      .sort((a, b) => (a.dueAt || 0) - (b.dueAt || 0));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, allWords.length === 0]); // only recompute if deck changes or words go from 0 to non-zero

  // Local queue of word IDs still needing review this session.
  // Cards rated "again" go back to the end; cards pushed > 24h out leave.
  const [queue, setQueue] = useState<string[]>(() => initialQueue.map(w => w.id));
  const [cardIndex, setCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [reviewedCount, setReviewedCount] = useState(0);

  // Build a lookup from the snapshot so card data stays stable
  const wordMap = useMemo(() => {
    const map = new Map<string, SavedWord>();
    for (const w of allWords) map.set(w.id, w);
    return map;
  }, [allWords]);

  const totalInSession = queue.length;
  const currentWordId = queue[cardIndex];
  const current = currentWordId ? wordMap.get(currentWordId) : undefined;

  // Freeze the displayed word during flip-back animation so the next card's
  // back side isn't visible while the card rotates to front.
  const [frozenWord, setFrozenWord] = useState<SavedWord | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const displayedWord = frozenWord || current;

  // Track reviewed count in ref for cleanup
  const reviewedCountRef = useRef(0);
  reviewedCountRef.current = reviewedCount;

  useEffect(() => {
    return () => {
      if (reviewedCountRef.current > 0 && id) {
        updateDeckLastReviewed(id);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleRate = useCallback(async (rating: Rating) => {
    if (!current) return;

    const updates = computeSRS(current, rating);
    await updateWordSRS(current.id, updates);
    setReviewedCount(c => c + 1);

    const newDueAt = updates.dueAt;
    const stillDue = newDueAt <= Date.now() + DUE_THRESHOLD_MS;

    // Freeze the current card's content so it stays visible during flip-back
    setFrozenWord({ ...current });
    setIsTransitioning(true);
    setIsFlipped(false);

    // Wait for flip-back animation to complete, then swap content
    setTimeout(() => {
      setQueue(prev => {
        const next = [...prev];
        next.splice(cardIndex, 1);
        if (stillDue) {
          next.push(current.id);
        }
        if (next.length === 0) {
          setCompleted(true);
          if (id) updateDeckLastReviewed(id);
        } else {
          setCardIndex(i => (i >= next.length ? 0 : i));
        }
        return next;
      });
      setFrozenWord(null);
      setIsTransitioning(false);
    }, 380);
  }, [current, cardIndex, updateWordSRS, updateDeckLastReviewed, id]);

  const handleExit = useCallback(async () => {
    if (reviewedCount > 0 && id) {
      await updateDeckLastReviewed(id);
    }
    navigate(`/app/decks/${id}`);
  }, [reviewedCount, updateDeckLastReviewed, id, navigate]);

  if (!deck) {
    return (
      <div className="p-4 pt-14 md:p-8 text-center">
        <p className="text-slate-500 dark:text-slate-400">Deck not found.</p>
        <button onClick={() => navigate('/app/decks')} className="mt-4 text-primary-600 text-sm">Back to Decks</button>
      </div>
    );
  }

  if (allWords.length === 0) {
    return (
      <div className="p-4 pt-14 md:p-8 text-center">
        <p className="text-slate-500 dark:text-slate-400">No words to review in this deck.</p>
        <button onClick={() => navigate(`/app/decks/${id}`)} className="mt-4 text-primary-600 text-sm">Back to Deck</button>
      </div>
    );
  }

  if (initialQueue.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-4 pt-14 md:p-8">
        <div className="text-5xl mb-6">✨</div>
        <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">All caught up!</h2>
        <p className="text-slate-500 dark:text-slate-400 mb-6">No cards due for review in "{deck.name}"</p>
        <button
          onClick={() => navigate(`/app/decks/${id}`)}
          className="px-5 py-2.5 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-sm rounded-lg transition-colors"
        >
          Back to Deck
        </button>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-4 pt-14 md:p-8">
        <div className="text-5xl mb-6">🎉</div>
        <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">Review Complete!</h2>
        <p className="text-slate-500 dark:text-slate-400 mb-6">You reviewed {reviewedCount} card{reviewedCount !== 1 ? 's' : ''} from "{deck.name}"</p>
        <div className="flex gap-3">
          <button
            onClick={() => { setQueue(initialQueue.map(w => w.id)); setCardIndex(0); setIsFlipped(false); setCompleted(false); setReviewedCount(0); }}
            className="px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-lg transition-colors font-medium"
          >
            Review Again
          </button>
          <button
            onClick={() => navigate(`/app/decks/${id}`)}
            className="px-5 py-2.5 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-sm rounded-lg transition-colors"
          >
            Back to Deck
          </button>
        </div>
      </div>
    );
  }

  if (!displayedWord) return null;

  const previewIntervals = RATING_CONFIG.map(r => ({
    ...r,
    interval: formatInterval(computeSRS(displayedWord, r.key).interval),
  }));

  return (
    <div className="flex flex-col items-center p-4 pt-14 md:p-8">
      {/* Header with exit and progress */}
      <div className="w-full max-w-md flex items-center justify-between mb-6">
        <button
          onClick={handleExit}
          className="px-3 py-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
        >
          ← Exit
        </button>
        <div className="text-sm text-slate-400">
          {totalInSession} remaining — {deck.name}
        </div>
      </div>

      {/* Card — no AnimatePresence, just instant swap to front of next card */}
      <div className="w-full max-w-md mb-6" style={{ perspective: '1000px' }}>
        <motion.div
          className="relative w-full cursor-pointer"
          style={{ transformStyle: 'preserve-3d' }}
          animate={{ rotateY: isFlipped ? 180 : 0 }}
          transition={{ duration: 0.35 }}
          onClick={() => !isTransitioning && setIsFlipped(f => !f)}
        >
          {/* Front */}
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 p-10 flex flex-col items-center justify-center min-h-[280px]"
            style={{ backfaceVisibility: 'hidden' }}
          >
            <div className="text-4xl font-bold text-slate-800 dark:text-slate-100 mb-4">{displayedWord.text}</div>
            <p className="text-sm text-slate-400">Click to reveal</p>
          </div>

          {/* Back */}
          <div
            className="absolute inset-0 bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 p-10 flex flex-col items-center justify-center min-h-[280px]"
            style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
          >
            <div className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-2">{displayedWord.text}</div>
            <div className="text-xl text-primary-600 dark:text-primary-400 mb-3">{displayedWord.reading}</div>
            <div className="text-base text-slate-500 dark:text-slate-400 text-center leading-relaxed mb-2">{displayedWord.translation}</div>
            {displayedWord.exampleSentence && (
              <div className="text-sm text-slate-400 dark:text-slate-500 text-center mt-2 italic">
                {displayedWord.exampleSentence}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Rating buttons - show only when flipped */}
      {isFlipped && !isTransitioning ? (
        <div className="flex gap-2 flex-wrap justify-center">
          {previewIntervals.map(r => (
            <button
              key={r.key}
              onClick={(e) => { e.stopPropagation(); handleRate(r.key); }}
              className={`px-4 py-2 text-sm rounded-lg transition-colors font-medium border ${r.borderColor} ${r.textColor} ${r.hoverBg} bg-white dark:bg-slate-800`}
            >
              <div>{r.label}</div>
              <div className="text-[10px] opacity-60">{r.interval}</div>
            </button>
          ))}
        </div>
      ) : (
        // Invisible placeholder to prevent layout shift
        <div className="h-[52px]" />
      )}

      {/* Progress bar */}
      <div className="w-full max-w-md mt-6 bg-slate-200 dark:bg-slate-700 rounded-full h-1.5 overflow-hidden">
        <div
          className="bg-primary-600 h-full rounded-full transition-all duration-300"
          style={{ width: `${totalInSession > 0 ? ((initialQueue.length - totalInSession) / initialQueue.length) * 100 : 100}%` }}
        />
      </div>
    </div>
  );
}
